import AddIcon from '@mui/icons-material/Add';
import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import { Box, Button, CircularProgress, Divider, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography, Tooltip, FormControl, InputLabel, Select, MenuItem, Menu, ListItemIcon } from "@mui/material";
import { useEffect, useState } from "react";
import { FieldValues } from "react-hook-form";
import { useSnackbar } from "../../../core/contexts/SnackbarContext";
import { useProgramasFidelidade } from "../../hooks/useProgramasFidelidade";
import { useFetchProgramasFidelidade } from "../../hooks/useFetchProgramasFidelidade";
import { IProgramaFidelidade } from "../../interfaces/IProgramaFidelidade";
import { CadastrarEditarProgramaFidelidadeModal } from "./containers/CadastrarEditarProgramaFidelidadeModal";
import { ButtonsContainer, FiltrosContainer, Loading, NenhumPrograma, PageContainer, TableContainer } from "./styles";
import { useAutenticacaoContext } from '../../../core/contexts/AutenticacaoContext';
import { useNavigate } from 'react-router-dom';
import { ProgramaFidelidadeTableRow } from '../../components/ProgramaFidelidadeTableRow';

export const ListarProgramasFidelidade = () => {
    // Estados para seleção múltipla
    const [programasSelecionados, setProgramasSelecionados] = useState<string[]>([]);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const openAcoes = Boolean(anchorEl);

    const snackbar = useSnackbar();

    const [openCadastrar, setOpenCadastrar] = useState(false);
    const handleOpenCadastrar = () => setOpenCadastrar(true);
    const handleCloseCadastrar = () => setOpenCadastrar(false);

    const { criar, loading, ativar, inativar } = useProgramasFidelidade();
    const {
        dados,
        handleRefresh,
        handleLimpar,
        handleAtivoChange,
        isLoading,
        ativo,
        handleFiltrar
    } = useFetchProgramasFidelidade();

    // Funções para seleção múltipla
    const toggleProgramaSelecionado = (uuid: string) => {
        setProgramasSelecionados(prev =>
            prev.includes(uuid) ? prev.filter(id => id !== uuid) : [...prev, uuid]
        );
    };

    const limparSelecao = () => {
        setProgramasSelecionados([]);
    };

    const abrirAcoes = (event: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget);
    const fecharAcoes = () => setAnchorEl(null);

    const handleCancelarCadastrar = () => {
        handleCloseCadastrar();
    }

    const handleCadastrarPrograma = async (programaData: FieldValues) => {
        if (loading) {
            return;
        }

        const programa: IProgramaFidelidade = {
            tipo: programaData.tipo,
            porcentagemRetorno: programaData.porcentagemRetorno,
            produtoUuids: programaData.produtoUuids,
            quantidadePedidosNecessarios: programaData.quantidadePedidosNecessarios,
            diasVencimentoCashback: programaData.diasVencimentoCashback ?? null,
            limiteAcumuloCashback: programaData.limiteAcumuloCashback ?? null,
            ativo: true
        } as IProgramaFidelidade;

        const programaCadastrado = await criar(programa);

        if (!programaCadastrado) {
            return snackbar("Ocorreu um erro ao cadastrar o programa de fidelidade", { severity: "error" })
        }

        handleRefresh();
        handleCancelarCadastrar();
        return snackbar("Programa de fidelidade cadastrado com sucesso", { severity: "success" })
    }

    const handleAtivarProgramasSelecionados = async () => {
        if (programasSelecionados.length === 0) {
            return;
        }

        if (loading) {
            return;
        }

        let sucessos = 0;
        let falhas = 0;

        for (const uuid of programasSelecionados) {
            try {
                const resultado = await ativar(uuid);
                if (resultado) {
                    sucessos++;
                } else {
                    falhas++;
                }
            } catch (error) {
                falhas++;
            }
        }

        if (falhas > 0) {
            snackbar(`${sucessos} programa(s) ativado(s) com sucesso. ${falhas} falha(s).`, { 
                severity: falhas === programasSelecionados.length ? "error" : "warning" 
            });
        } else {
            snackbar(`${sucessos} programa(s) ativado(s) com sucesso`, { severity: "success" });
        }

        handleRefresh();
        limparSelecao();
        fecharAcoes();
    }

    const handleInativarProgramasSelecionados = async () => {
        if (programasSelecionados.length === 0) {
            return;
        }

        if (loading) {
            return;
        }

        let sucessos = 0;
        let falhas = 0;

        for (const uuid of programasSelecionados) {
            try {
                const resultado = await inativar(uuid);
                if (resultado) {
                    sucessos++;
                } else {
                    falhas++;
                }
            } catch (error) {
                falhas++;
            }
        }

        if (falhas > 0) {
            snackbar(`${sucessos} programa(s) inativado(s) com sucesso. ${falhas} falha(s).`, { 
                severity: falhas === programasSelecionados.length ? "error" : "warning" 
            });
        } else {
            snackbar(`${sucessos} programa(s) inativado(s) com sucesso`, { severity: "success" });
        }

        handleRefresh();
        limparSelecao();
        fecharAcoes();
    }

    const navigate = useNavigate();
    const { verificarAcessoModulo, perfil } = useAutenticacaoContext();
    useEffect(() => {
        if(perfil && !verificarAcessoModulo("Cardápio")) {
            navigate("/nao-autorizado")
        }
    }, [perfil])
    
    const programas = Array.isArray(dados) ? dados : [];

    // Verificar status dos programas selecionados
    const programasSelecionadosData = programas.filter((p: IProgramaFidelidade) => 
        p.uuid && programasSelecionados.includes(p.uuid)
    );
    
    const todosAtivos = programasSelecionadosData.length > 0 && 
        programasSelecionadosData.every((p: IProgramaFidelidade) => p.ativo);
    
    const todosInativos = programasSelecionadosData.length > 0 && 
        programasSelecionadosData.every((p: IProgramaFidelidade) => !p.ativo);
    
    const temMistura = programasSelecionadosData.length > 0 && 
        !todosAtivos && !todosInativos;
    
    return (
        <PageContainer>
            <CadastrarEditarProgramaFidelidadeModal
                open={openCadastrar}
                handleCadastrarPrograma={handleCadastrarPrograma}
                handleCancelar={handleCancelarCadastrar}
                loading={loading}
            />
            <FiltrosContainer>
                <Typography variant='h6'>Programas de Fidelidade</Typography>
                <Box className="filtros-programas">
                    <FormControl size="small" className='input-programa'>
                        <InputLabel>Status</InputLabel>
                        <Select
                            value={ativo === undefined ? '' : ativo ? 'ativo' : 'inativo'}
                            label="Status"
                            onChange={(e) => {
                                const value = e.target.value;
                                handleAtivoChange(value === '' ? undefined : value === 'ativo');
                            }}
                        >
                            <MenuItem value="">Todos</MenuItem>
                            <MenuItem value="ativo">Ativo</MenuItem>
                            <MenuItem value="inativo">Inativo</MenuItem>
                        </Select>
                    </FormControl>
                    <ButtonsContainer>
                        <Button
                            variant="outlined"
                            onClick={handleLimpar}
                        >
                            Limpar
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleFiltrar}
                        >
                            Filtrar
                        </Button>
                        <Divider orientation="vertical" flexItem sx={{ mr: "0.5rem", ml: "0.5rem" }} />
                        <Button
                            disabled={programasSelecionados.length <= 0}
                            variant="contained"
                            endIcon={
                                <ArrowDropDown sx={{ transform: openAcoes ? 'rotate(-180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }} />
                            }
                            onClick={abrirAcoes}
                        >
                            Ações
                        </Button>
                        <Menu
                            anchorEl={anchorEl}
                            open={openAcoes}
                            onClose={fecharAcoes}
                        >
                            <MenuItem disabled>
                                <Typography variant="inherit" noWrap>
                                    {programasSelecionados.length} programa{programasSelecionados.length !== 1 ? 's' : ''} selecionado{programasSelecionados.length !== 1 ? 's' : ''}
                                </Typography>
                            </MenuItem>
                            <Divider />
                            {temMistura && (
                                <MenuItem disabled>
                                    <Typography variant="inherit" color="error" noWrap>
                                        Selecione apenas programas com o mesmo status
                                    </Typography>
                                </MenuItem>
                            )}
                            {todosInativos && (
                                <MenuItem onClick={handleAtivarProgramasSelecionados}>
                                    <Typography variant="inherit">Ativar</Typography>
                                </MenuItem>
                            )}
                            {todosAtivos && (
                                <MenuItem onClick={handleInativarProgramasSelecionados}>
                                    <Typography variant="inherit">Inativar</Typography>
                                </MenuItem>
                            )}
                        </Menu>
                        <Divider orientation="vertical" flexItem sx={{ mr: "0.5rem", ml: "0.5rem" }} />
                        <Tooltip title="Cadastre um novo programa de fidelidade">
                            <Button
                                onClick={handleOpenCadastrar}
                                variant="contained"
                                startIcon={<AddIcon />}
                            >
                                Novo
                            </Button>
                        </Tooltip>
                    </ButtonsContainer>
                </Box>
            </FiltrosContainer>
            <Paper sx={{
                padding: "1rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                margin: "0 auto",
                width: "85%"
            }}>
                <TableContainer>
                    {isLoading
                        ? (
                            <Loading>
                                <CircularProgress />
                            </Loading>
                        )
                        : programas && programas.length ? (
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell padding="checkbox">
                                        </TableCell>
                                        <TableCell align="left" width="25%">Tipo</TableCell>
                                        <TableCell align="center" width="15%">Status</TableCell>
                                        <TableCell align="left" width="35%">Configuração</TableCell>
                                        <TableCell align="left" width="25%">Data de Cadastro</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {programas.map((programa: IProgramaFidelidade) => (
                                        <ProgramaFidelidadeTableRow
                                            key={programa.uuid || 'temp-key'}
                                            programa={programa}
                                            selecionado={programa.uuid ? programasSelecionados.includes(programa.uuid) : false}
                                            toggleSelecionado={toggleProgramaSelecionado}
                                        />
                                    ))}
                                </TableBody>
                            </Table>
                        ) : (
                            <NenhumPrograma>
                                <Typography>
                                    Nenhum programa de fidelidade cadastrado
                                </Typography>
                            </NenhumPrograma>
                        )}
                </TableContainer>
            </Paper>
        </PageContainer>
    );
}

